import url from 'url';
import queryString from 'query-string';
import assert from 'assert';
import lodashXor from 'lodash/fp/xor';
import lodashPadStart from 'lodash/padStart';
import typeCheck from 'common/util/typeCheck';
import round from 'common/util/round';

import {
  STRIPE_CHARGE_CATEGORY_APPLICATION_FEE,
  STRIPE_CHARGE_CATEGORY_SECURITY_DEPOSIT,
  STRIPE_CHARGE_CATEGORY_GUARANTOR_APPLICATION_FEE,
  STRIPE_FEE_PERCENTAGE_BANK,
  STRIPE_FEE_PERCENTAGE_CARD,
  STRIPE_CARD_ERROR_CODE_INSUFFICIENT_FUNDS,
  STRIPE_CARD_ERROR_MESSAGE_INSUFFICIENT_FUNDS,
  STRIPE_CARD_ERROR_MESSAGE_GENERIC_DECLINE,
  STRIPE_ERROR_CODE_CARD_DECLINED,
  STRIPE_CARD_CODE_ERROR_MAPPING,
} from 'common/stripeConstants';
import {
  SR_STRIPE_CARD_INSUFFICIENT_FUNDS,
  SR_STRIPE_CARD_GENERIC_DECLINE_ERROR,
} from 'common/errorCodes';

export function makeStripeCardExpirationString(card) {
  return `${lodashPadStart(String(card.exp_month), 2, '0')}/${String(card.exp_year).slice(-2)}`;
}

export function makeStripeChargeConcept(params) {
  if (params.category === STRIPE_CHARGE_CATEGORY_APPLICATION_FEE) {
    const { applicationId, listingId, isSunroomSelect, serviceType } = params;
    typeCheck('applicationId::Maybe PositiveNumber', applicationId);
    typeCheck('listingId::PositiveNumber', listingId);
    typeCheck('isSunroomSelect::Boolean', isSunroomSelect);
    typeCheck('serviceType::NonEmptyString', serviceType);

    return (
      `${serviceType} | ` +
      `Application Fee (${applicationId ? `App ID: ${applicationId} - ` : ''}Listing ID: ${listingId}) ` +
      `- SR Select: ${isSunroomSelect ? 'Yes' : 'No'}`
    );
  }

  if (params.category === STRIPE_CHARGE_CATEGORY_GUARANTOR_APPLICATION_FEE) {
    const { applicationId } = params;
    return `Guarantor Application Fee (Application ID: ${applicationId})`;
  }

  if (params.category === STRIPE_CHARGE_CATEGORY_SECURITY_DEPOSIT) {
    const { userName, userPhone, listingAddress, transferToStripeAccountId } = params;
    typeCheck('userName::NonEmptyString', userName);
    typeCheck('userPhone::NonEmptyString', userPhone);
    typeCheck('listingAddress::NonEmptyString', listingAddress);
    typeCheck('transferToStripeAccountId::Maybe PositiveNumber', transferToStripeAccountId);

    return (
      `Security Deposit — ${userName} (${userPhone}) — ${listingAddress}`
      + (transferToStripeAccountId ? ` - ${transferToStripeAccountId}` : '')
    );
  }

  const error = new Error(`"${params.category}" is not supported.`);
  error.name = 'SrMakeStripeChargeConceptError';
  throw error;
}

export function makeStripeConnectedAccountOnboardingLink({
  appUrl,
  listingId,
  partnerId,
}) {
  typeCheck('listingId::Maybe PositiveNumber', listingId);
  typeCheck('partnerId::Maybe PositiveNumber', partnerId);
  assert(
    lodashXor(!!partnerId, !!listingId),
    'One of `partnerId` or `listingId` is required, but not both.'
  );

  const path = listingId
    ? `/connect-bank/listing/${listingId}/start`
    : `/connect-bank/partner/${partnerId}/start`;

  return url.resolve(appUrl, path);
}

export function makeStripeConnectedAccountFormLink({
  appUrl = '/',
  listingId,
  partnerId,
  locationQuery = {},
}) {
  typeCheck('listingId::Maybe PositiveNumber', listingId);
  typeCheck('partnerId::Maybe PositiveNumber', partnerId);
  typeCheck('locationQuery::Object', locationQuery);
  assert(
    lodashXor(!!partnerId, !!listingId),
    'One of `partnerId` or `listingId` is required, but not both.'
  );

  let path = listingId
    ? `/connect-bank/listing/${listingId}/form`
    : `/connect-bank/partner/${partnerId}/form`;

  if (Object.keys(locationQuery).length) {
    path += `?${queryString.stringify(locationQuery)}`;
  }

  return url.resolve(appUrl, path);
}

export const getStripeFee = (chargeAmount, percentage = STRIPE_FEE_PERCENTAGE_CARD) => {
  if (typeof chargeAmount !== 'number') {
    return 0;
  }
  const stripeFee = round(chargeAmount * percentage);

  return {
    chargeAmount,
    stripeFee,
    totalAmount: round(chargeAmount + stripeFee),
  };
};

export const getCardStripeFee = (chargeAmount) => {
  return getStripeFee(chargeAmount, STRIPE_FEE_PERCENTAGE_CARD);
};

export const getBankStripeFee = (chargeAmount) => {
  return getStripeFee(chargeAmount, STRIPE_FEE_PERCENTAGE_BANK);
};

export const getStripeCardErrorMessage = (stripeError) => {
  let name = SR_STRIPE_CARD_GENERIC_DECLINE_ERROR;
  let message = STRIPE_CARD_ERROR_MESSAGE_GENERIC_DECLINE;

  if (!stripeError) {
    return { name, message };
  }

  if (stripeError.code === STRIPE_ERROR_CODE_CARD_DECLINED) {
    if (stripeError.decline_code === STRIPE_CARD_ERROR_CODE_INSUFFICIENT_FUNDS) {
      name = SR_STRIPE_CARD_INSUFFICIENT_FUNDS;
      message = STRIPE_CARD_ERROR_MESSAGE_INSUFFICIENT_FUNDS;
    }
    return { name, message };
  }

  const mappedError = STRIPE_CARD_CODE_ERROR_MAPPING[stripeError.code];
  if (mappedError) {
    return {
      name: mappedError.name,
      message: mappedError.message,
    };
  }
  return { name, message };
};
